import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link, NavLink } from 'react-router-dom';
import {
  Collapse,
  Navbar,
  NavbarToggler,
  Nav,
  NavItem,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';

import logo from '../../../assets/images/ae-logo.svg';
import logoColor from '../../../assets/images/ae-logo-color.svg';
import './Header.scss';
import { useIntl, FormattedMessage } from 'react-intl';

const Header = ({ page, language, changeLanguage }) => {
  const intl = useIntl();
  const languageLabel =
    language === 'EN'
      ? intl.formatMessage({
          id: 'translate-link-english',
          defaultMessage: 'English',
        })
      : intl.formatMessage({
          id: 'translate-link-spanish',
          defaultMessage: 'Spanish',
        });
  const [collapse, setCollapse] = useState(false);
  const isHomepage =
    window.location.pathname === '/' || window.location.pathname === null;

  return (
    <div className={`Header ${isHomepage ? 'black' : 'white'}`}>
      <div className="container" style={{ padding: 0 }}>
        <Navbar expand="lg">
          <div className="site-logo">
            <Link
              to="/"
              className="navbar-brand"
              aria-label="Homepage"
              onClick={() => setCollapse(false)}
            >
              <img
                src={isHomepage ? logo : logoColor}
                className="img-fluid"
                alt="Austin Energy EV Buyer's Guide Homepage"
              />
            </Link>
          </div>
          <NavbarToggler
            onClick={() => setCollapse(!collapse)}
            aria-label={collapse ? 'Close navigation' : 'Open navigation'}
          >
            <span style={{ display: 'none' }}>Mobile Toggle Menu</span>
            <span className="navbar-toggler-icon"></span>
          </NavbarToggler>
          <Collapse isOpen={collapse} navbar>
            <Nav className="ml-auto" navbar>
              {process.env.REACT_APP_PAGES_HOME_ENABLED ? (
                <NavItem>
                  <Link
                    aria-label="Link to Homepage"
                    to="/"
                    className={isHomepage ? 'active' : ''}
                  >
                    <span>
                      <FormattedMessage
                        id="home"
                        defaultMessage="Home"
                        description="Home Header"
                      />
                    </span>
                  </Link>
                </NavItem>
              ) : null}
              <UncontrolledDropdown nav inNavbar>
                <DropdownToggle
                  nav
                  caret
                  className={page === 'dealers' ? 'active' : ''}
                >
                  <span>
                    <FormattedMessage
                      id="shopLocal"
                      defaultMessage="Shop Local EVs"
                      description="Shop Local EVs Header"
                    />
                  </span>
                </DropdownToggle>
                <DropdownMenu className="text-center">
                  <DropdownItem
                    role="menuitem"
                    key={`nav-item-browse-nearby-inventory`}
                    title={page === 'dealer' ? 'Active Page' : null}
                    tag={NavLink}
                    to={'/inventory'}
                    aria-label="Link to Inventory"
                  >
                    <FormattedMessage
                      id="browseInventory"
                      defaultMessage="Browse Nearby Inventory"
                      description="Browse Nearby Inventory Header"
                    />
                  </DropdownItem>
                  <DropdownItem
                    role="menuitem"
                    key={`nav-item-dealers`}
                    title={page === 'dealer' ? 'Active Page' : null}
                    tag={NavLink}
                    to={'/dealers'}
                    aria-label="Link to Dealers"
                  >
                    <FormattedMessage
                      id="dealerDirectory"
                      defaultMessage="Local Dealers"
                      description="Dealer Directory Header"
                    />
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
              <NavItem>
                <NavLink
                  title={page === 'vehicles' ? 'Active Page' : null}
                  to="/vehicles"
                  aria-label="Link to Vehicles"
                >
                  <span>
                    <FormattedMessage
                      id="electricVehicles"
                      defaultMessage="Electric Vehicles"
                      description="Electric Vehicles Header"
                    />
                  </span>
                </NavLink>
              </NavItem>
              {process.env.REACT_APP_PAGES_USED_VEHICLES_ENABLED ? (
                <NavItem>
                  <NavLink
                    title={page === 'vehicles' ? 'Active Page' : null}
                    to="/used-vehicles"
                  >
                    <span>
                      <FormattedMessage
                        id="usedElectricVehicles"
                        defaultMessage="Used Vehicles"
                        description="Used Vehicles Header"
                      />
                    </span>
                  </NavLink>
                </NavItem>
              ) : null}
              {process.env.REACT_APP_PAGES_INCENTIVES_ENABLED ? (
                <NavItem>
                  <NavLink
                    title={page === 'incentives' ? 'Active Page' : null}
                    to="/incentives"
                    aria-label="Link to Incentives"
                  >
                    <span>
                      <FormattedMessage
                        id="incentives"
                        defaultMessage="Incentives"
                        description="Incentives Header"
                      />
                    </span>
                  </NavLink>
                </NavItem>
              ) : null}
              {process.env.REACT_APP_PAGES_CHARGING_ENABLED ? (
                <NavItem>
                  <NavLink
                    title={page === 'map' ? 'Active Page' : null}
                    to="/charging-stations"
                    aria-label="Link to Charging Stations"
                  >
                    <span>
                      <FormattedMessage
                        id="chargingStations"
                        defaultMessage="Charging Stations"
                        description="Charging Stations Header"
                      />
                    </span>
                  </NavLink>
                </NavItem>
              ) : null}
              {process.env.REACT_APP_PAGES_ELECTRICIANS_ENABLED ? (
                <NavItem>
                  <NavLink
                    title={page === 'electricians' ? 'Active Page' : null}
                    to="/electricians"
                  >
                    <span>
                      <FormattedMessage
                        id="electricians"
                        defaultMessage="Electricians"
                        description="Electricians Header"
                      />
                    </span>
                  </NavLink>
                </NavItem>
              ) : null}
              {process.env.REACT_APP_PAGES_FAQ_ENABLED ? (
                <NavItem>
                  <NavLink
                    title={page === 'faq' ? 'Active Page' : null}
                    to="/faq"
                    aria-label="Link to FAQ"
                  >
                    <span>
                      <FormattedMessage
                        id="faq"
                        defaultMessage="FAQ"
                        description="FAQ Header"
                      />
                    </span>
                  </NavLink>
                </NavItem>
              ) : null}
              {process.env.REACT_APP_PAGES_ERIDES_ENABLED ? (
                <NavItem>
                  <NavLink
                    title={page === 'e-ride' ? 'Active Page' : null}
                    to="/e-ride"
                    aria-label="Link to E-Ride"
                  >
                    <span>
                      <FormattedMessage
                        id="e-ride"
                        defaultMessage="E-Ride"
                        description="E-Ride Header"
                      />
                    </span>
                  </NavLink>
                </NavItem>
              ) : null}

              <UncontrolledDropdown nav inNavbar>
                <DropdownToggle nav caret className="active">
                  <span>{languageLabel}</span>
                </DropdownToggle>
                <DropdownMenu className="text-center">
                  {language === 'ES' && (
                    <DropdownItem
                      role="menuitem"
                      key="nav-item-language-english"
                      tag={NavLink}
                      to="#"
                      aria-label="Change to English Language"
                      onClick={() => changeLanguage('EN')}
                    >
                      <FormattedMessage
                        id="translate-link-english"
                        defaultMessage="English"
                        description="English Header"
                      />
                    </DropdownItem>
                  )}

                  {language === 'EN' && (
                    <DropdownItem
                      role="menuitem"
                      key="nav-item-language-spanish"
                      tag={NavLink}
                      to="#"
                      aria-label="Change to Spanish Language"
                      onClick={() => changeLanguage('ES')}
                    >
                      <FormattedMessage
                        id="translate-link-spanish"
                        defaultMessage="Spanish"
                        description="Spanish Header"
                      />
                    </DropdownItem>
                  )}
                </DropdownMenu>
              </UncontrolledDropdown>
            </Nav>
          </Collapse>
        </Navbar>
      </div>
    </div>
  );
};

export default Header;

Header.propTypes = {
  page: PropTypes.string,
};
